import {
  AppLayout,
  HeaderSearch,
  LanguageSwitch,
  Logo,
  ProfileMenu,
  SidebarHelp,
  toast,
  type NavItem,
} from '@hemmafixa/ui';
import { LogOut, Settings } from 'lucide-react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useCurrentUser, useLogout } from '@/features/auth/api';
import { authErrorMessage } from '@/features/auth/auth-errors';
import { useAccountPreferences } from '@/features/auth/use-account-preferences';
import { useI18n } from '@/i18n/context';
import { env } from '@/lib/env';
import { usePanelCollapsed } from '@/lib/preferences';
import { useOpenReportCount, useProviderCounts, useReviewCount } from '@/features/admin/api';
import { useChatSocket, useUnreadMessages } from '@/features/messages/api';
import { NotificationBell } from '@/features/notifications/notification-bell';
import { activeEntry, navigation, searchPath, type Area, type NavBadge } from './navigation';

export function AppShell({ area }: { area: Area }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const user = useCurrentUser();
  const preferences = useAccountPreferences(user);
  const panel = usePanelCollapsed();
  const logout = useLogout();
  const jobsInReview = useReviewCount(area === 'admin');
  const providerCounts = useProviderCounts(area === 'admin');
  const openReports = useOpenReportCount(area === 'admin');
  const chat = area === 'client' || area === 'provider';
  useChatSocket(chat);
  const unreadMessages = useUnreadMessages(chat);
  const badgeCounts: Record<NavBadge, number | undefined> = {
    jobsInReview: jobsInReview.data,
    providersPending: providerCounts.data?.pending,
    unreadMessages: unreadMessages.data || undefined,
    openReports: openReports.data || undefined,
  };

  const logOut = () =>
    logout.mutate(undefined, { onError: (error) => toast.error(authErrorMessage(error, t)) });

  const nav: NavItem[] = navigation[area].map((entry) => ({
    label: t.nav[entry.label],
    href: entry.path,
    icon: entry.icon,
    badge: entry.badge ? badgeCounts[entry.badge] : undefined,
    group: entry.group,
  }));
  // The notifications page is reached from the bell, not the menu, so no menu entry is highlighted there
  const onNotifications = pathname === `/${area}/notifications`;
  const active = onNotifications ? undefined : activeEntry(area, pathname);
  const searchTarget = searchPath[area];

  return (
    <AppLayout
      brand={<Logo />}
      brandCollapsed={<Logo variant="mark" />}
      areaLabel={t.areas[area]}
      collapsed={preferences.collapsed}
      onCollapsedChange={preferences.setCollapsed}
      panelCollapsed={panel.collapsed}
      onPanelCollapsedChange={panel.setCollapsed}
      nav={nav}
      activeHref={active?.path ?? ''}
      renderLink={(item, content, className) => (
        <Link
          to={item.href}
          className={className}
          aria-current={item.href === active?.path ? 'page' : undefined}
        >
          {content}
        </Link>
      )}
      title={onNotifications ? t.notifications.title : active ? t.nav[active.label] : 'Hemmafixa'}
      labels={t.layout}
      sidebarFooter={(iconsOnly) => (
        <SidebarHelp
          iconsOnly={iconsOnly}
          title={t.layout.helpTitle}
          text={t.layout.helpText}
          actionLabel={t.layout.contactSupport}
          href={`mailto:${env.supportEmail}`}
        />
      )}
      search={
        searchTarget && (
          <HeaderSearch
            key={area}
            onSearch={(query) => navigate(`${searchTarget}?q=${encodeURIComponent(query)}`)}
            labels={{
              search: t.layout.search,
              placeholder: t.layout.searchPlaceholder,
              close: t.layout.closeSearch,
            }}
          />
        )
      }
      headerActions={
        <>
          <NotificationBell area={area} />
          <LanguageSwitch
            value={preferences.language}
            onChange={preferences.setLanguage}
            label={t.layout.language}
          />
          <ProfileMenu
            name={user.name}
            avatarSeed={user.id}
            email={user.email}
            label={t.layout.account}
            items={[
              { label: t.layout.settings, icon: Settings, onSelect: () => navigate(`/${area}/settings`) },
              {
                label: t.layout.logOut,
                icon: LogOut,
                tone: 'danger' as const,
                onSelect: logOut,
              },
            ]}
          />
        </>
      }
    >
      <Outlet />
    </AppLayout>
  );
}
